@if (loading) {
<spinner-overlay [fullscreen]="true"></spinner-overlay>
}
<app-menu [organisation]="organisation" [screen]="screen" (menuEvent)="handleFunctionEvent($event)"></app-menu>
<app-analysis-header [organisation]="organisation" view="overview"></app-analysis-header>

@if (isHelpOn()) {
<div class="hint">
    The headline figures for what you're looking at, what to do first, and how each model is doing. Choose a workspace
    and models at the top; every view keeps the same choice. Click a figure or a recommendation to see it in the view
    that explains it, or a model to look at just that model.
</div>
}

<div class="overview" data-testid="overview">
    @if (error) {
    <p class="error" role="alert">{{ error }}</p>
    } @else if (organisation) {
    <header class="intro">
        <h2>{{ workspaceName }}: {{ scopeDescription }}</h2>
        <p>{{ models.length }} {{ models.length === 1 ? 'model' : 'models' }} · what it costs, how the work is done and what to change, from the workforce, systems and EPM analyses.@if (compared) { Each figure is compared with the current state: green is an improvement, red a decline.}</p>
    </header>

    <!-- the headline figures -->
    <section id="overview-tiles" class="tiles" aria-label="Headline figures">
        @for (h of headlines; track h.label) {
        <button type="button" class="tile" [class]="'tile ' + h.tone" (click)="openLens(h.lens)" [matTooltip]="'See it in ' + h.lens" data-testid="overview-tile">
            <span class="label">{{ h.label }}</span>
            <strong>{{ h.value }}</strong>
            <span class="sub">{{ h.detail }}</span>
            @if (h.change) {<span class="vs">@if (h.before) {Current state {{ h.before }}}<span [class]="'chg ' + h.change.tone">{{ h.change.text }}</span></span>}
        </button>
        }
    </section>

    <!-- what to do first -->
    <section id="overview-recommendations" aria-labelledby="ov-recommendations">
        <div class="section-head">
            <h3 id="ov-recommendations">What to do first</h3>
            <div class="lever-filter" role="group" aria-label="Filter by kind of change">
                @for (l of leversPresent(); track l) {
                <button type="button" class="chip" [class.active]="leverFilter === l" (click)="toggleLever(l)">{{ leverLabels[l] }}</button>
                }
            </div>
        </div>
        <p class="ov-hint">One list from every analysis, strongest first. Where two analyses point at the same thing, they're shown together as evidence.
            Tick the ones to try, then create a future state: AI makes the changes, with anything else you describe, such as a new system or an AI agent,
            in a new project{{ canChangeWorkspace ? ' or in the project you\'re looking at, after showing you what it would change' : '' }}. Current state is only changed by merging a project into it.</p>
        <div id="overview-future-state" class="future-bar">
            <button mat-flat-button color="accent" (click)="openFutureState()" [disabled]="!canCreateFutureState" disabledInteractive
                [matTooltip]="futureStateTooltip" data-testid="overview-future-state">
                <mat-icon>auto_awesome</mat-icon>Create a future state{{ selected.size ? ' from ' + selected.size + (selected.size === 1 ? ' recommendation' : ' recommendations') : '' }}</button>
            @if (canCreateFutureState && recommendations.length && !allSelected) {<button mat-button (click)="selectAll()" data-testid="overview-select-all-recommendations">Select all {{ filteredCount }}</button>}
            @if (selected.size) {<button mat-button (click)="selected.clear()">Clear</button>}
        </div>
        @if (recommendations.length) {
        <ol class="recommendations" data-testid="overview-recommendations">
            @for (r of shownRecommendations; track r.key) {
            <li [class.urgent]="r.urgent">
                <div class="rank">
                    @if (canCreateFutureState) {<mat-checkbox [checked]="selected.has(r.key)" (change)="toggleSelected(r, $event.checked)" [attr.aria-label]="'Include ' + r.title + ' in a future state'" data-testid="overview-select-recommendation"></mat-checkbox>}
                    <span class="priority" [matTooltip]="r.why">{{ r.priority }}</span>@if (r.urgent) {<span class="now">Act now</span>}</div>
                <div class="what">
                    <div class="chips"><span [class]="'lever ' + r.lever">{{ leverLabels[r.lever] }}</span>@if (r.effort) {<span class="effort">{{ r.effort }} effort</span>}
                        <span class="source" [matTooltip]="'Found by: ' + sourceList(r.sources)">{{ r.sources.length === 1 ? sourceLabels[r.sources[0]] : r.sources.length + ' analyses agree' }}</span></div>
                    <strong class="with-context">{{ r.title }} @if (r.lever !== 'data' && organisation) {<app-context-graph-button [organisation]="organisation" [scope]="scope" [request]="{recommendation: r}" [name]="r.title"></app-context-graph-button>}</strong>
                    <p>{{ r.detail }}</p>
                    @if (r.evidence.length) {
                    <ul class="evidence">@for (e of r.evidence.slice(0, 3); track $index) {<li>{{ e }}</li>}</ul>
                    }
                    @if (r.models.length) {<p class="models">{{ r.models.slice(0, 3).join(', ') }}{{ r.models.length > 3 ? ' and ' + (r.models.length - 3) + ' more' : '' }}</p>}
                </div>
                <div class="size">
                    @if (r.hours) {<strong>{{ hours(r.hours) }} h</strong><span>a year</span>}
                    @if (r.cost) {<span>about {{ money(r.cost) }}</span>}
                    <button mat-stroked-button color="accent" (click)="openRecommendation(r)" data-testid="overview-open-recommendation">See in {{ r.lens }}</button>
                </div>
            </li>
            }
        </ol>
        @if (filteredCount > 8) {
        <button mat-button (click)="allRecommendations = !allRecommendations">{{ allRecommendations ? 'Show fewer' : 'Show all ' + filteredCount + (hiddenUrgent ? ' (' + hiddenUrgent + ' more to act on now)' : '') }}</button>
        }
        } @else {
        <p class="none">Nothing stands out: roles have the capacity for their work, information moves between systems automatically and the outputs are valued.</p>
        }
    </section>

    <!-- the models -->
    @if (rows.length > 1) {
    <section id="overview-models-section" aria-labelledby="ov-models">
        <h3 id="ov-models">How each model is doing</h3>
        <p class="ov-hint">Click a model to look at just that model in every view.</p>
        <div class="scroll">
            <table class="models-table" data-testid="overview-models">
                <thead><tr><th>Model</th><th class="num">People's hours</th><th class="num">Cost</th><th class="num">Automated</th><th class="num">Roles over capacity</th>
                    <th class="num">Manual interfaces</th><th class="num" matTooltip="The cost of the steps that are exception work: on a detour from the main flow, or marked as such. Friction on the EPM scorecard">Exception work</th><th class="num">Outputs</th><th class="num">To do</th></tr></thead>
                <tbody>
                    @for (row of shownRows; track row.key) {
                    <tr (click)="openModel(row)" tabindex="0" (keydown.enter)="openModel(row)" [class.has-urgent]="row.urgent > 0">
                        <td><span class="swatch" [style.background]="row.colour"></span>{{ row.name }}</td>
                        <td class="num">{{ hours(row.hours) }}@if (row.changes?.hours; as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}</td>
                        <td class="num">{{ money(row.cost) }}@if (row.changes?.cost; as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}</td>
                        <td class="num">{{ percent(row.automated) }}</td>
                        <td class="num" [class.bad]="row.overRoles > 0">{{ row.overRoles || '–' }}</td>
                        <td class="num" [class.warn]="row.manualInterfaces > 0">{{ row.manualInterfaces || '–' }}</td>
                        <td class="num" [class.warn]="row.exceptionActions > 0" [matTooltip]="row.exceptionActions ? hours(row.exceptionHours) + ' hours a year in ' + row.exceptionActions + (row.exceptionActions === 1 ? ' step' : ' steps') : ''">{{ row.exceptionActions ? (row.exceptionCost ? money(row.exceptionCost) : hours(row.exceptionHours) + ' h') : '–' }}</td>
                        <td class="num">{{ row.outputs }}</td>
                        <td class="num">{{ row.recommendations || '–' }}@if (row.urgent) {<span class="now small">· {{ row.urgent }} now</span>}</td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>
        @if (rows.length > 15) {
        <button mat-button (click)="allRows = !allRows">{{ allRows ? 'Show fewer' : 'Show all ' + rows.length + ' models' }}</button>
        }
    </section>
    }

    <!-- the data to fix -->
    @if (dataGaps.length || epmDataGaps.length) {
    <section id="overview-data" aria-labelledby="ov-data">
        <h3 id="ov-data">Data to fix</h3>
        <p class="ov-hint">The figures above are only as good as the model. These gaps are the ones that would change them most.</p>
        <ul class="gaps" data-testid="overview-data-gaps">
            @for (g of dataGaps; track g.key) {<li><mat-icon>info</mat-icon><div><strong>{{ g.title }}</strong><p>{{ g.detail }}</p></div></li>}
            @for (g of epmDataGaps; track $index) {<li><mat-icon>info</mat-icon><div><p>{{ g }}</p></div></li>}
        </ul>
    </section>
    }

    <!-- the deeper views -->
    <section id="overview-deeper" aria-labelledby="ov-more">
        <h3 id="ov-more">Look deeper</h3>
        <div class="deeper">
            <button type="button" class="card" (click)="openLens('actions')"><mat-icon>account_tree</mat-icon><strong>Actions</strong><span>How work flows from step to step, with the EPM scorecard, heatmaps and the simulation.</span></button>
            <button type="button" class="card" (click)="openLens('people')"><mat-icon>groups</mat-icon><strong>People</strong><span>How roles hand work to each other, with the workforce report.</span></button>
            <button type="button" class="card" (click)="openLens('systems')"><mat-icon>dns</mat-icon><strong>Systems</strong><span>How information moves between systems, with the systems report.</span></button>
            <button type="button" class="card" (click)="openClassicInsights()" data-testid="overview-classic"><mat-icon>table_chart</mat-icon><strong>Tables and narrative</strong><span>Node tables, the written overview and the AI summary for what you're looking at.</span></button>
        </div>
    </section>
    }
</div>
